import * as React from 'react';
import { FavouriteDefinition } from 'app/types-shared';
import {
  useFavouriteLayoutAttributes, useRiskMatrixListingEvents, useSelectedDrillDown, useSelectors
} from 'app/area/case-report/risk-listing/hooks';
import { RiskReportResults } from 'app/area/case-report/risk-listing/types-store';
import { ElementLoader, InfoText, SelectSingle } from 'ui';
import caseListingstyles from './../case-listing/components/case-listing.scss';
import { CaseReportListingInformation } from 'app/area/case-report/components/case-report-listing-information';
import { AttributesPicker } from 'app/area/reports/containers/chart/components/attributes';
import { presentationType } from 'app/area/case-report/constants';
import styles from './risk-matrix-listing.scss';
import { useInstanceOptions } from 'app/area/favourite/favourite-context';
import { reportLayoutTypeContext } from 'app/area/reports/constants';
import { RiskMatrices } from 'app/area/case-report/risk-listing/risk-matrices';
import { transformLayoutOptionsForSelect } from 'app/area/reports/layoutTypesToComplextType';

import type { JSX } from "react";

interface RiskMatrixListingProps {
  translation: any;
  autoExecute: boolean;
  toggleAutoExecution: () => void;
  riskReportResults?: RiskReportResults;
  fetching: boolean;
  workingDefinition: FavouriteDefinition;
  hideVisualizationOptions: boolean;
  executeFailed?: JSX.Element;
  height?: number;
}

export const RiskMatrixListing = ({
  translation,
  autoExecute,
  riskReportResults,
  fetching,
  workingDefinition,
  executeFailed,
  hideVisualizationOptions
}: RiskMatrixListingProps) => {

  const {
    height,
    layoutTypeContextId = reportLayoutTypeContext, reportApiOverrides
  } = useInstanceOptions();

  const { onDrilldown, onUpdateLayoutAttributes, onLoadOptionsDrillDown, onSetDrillDownReport,
    onSetDrillDownLayoutType } = useRiskMatrixListingEvents(
      workingDefinition,
      autoExecute,
      layoutTypeContextId,
      riskReportResults,
      reportApiOverrides
    );

  const { layoutAttributes } = useFavouriteLayoutAttributes(layoutTypeContextId);
  const { selectedDrillDownReport, selectedDrillDownLayoutType } = useSelectedDrillDown();
  const { fetchingOptionsDrillDownTo, optionsDrillDownTo,
    fetchingOptionsDrillDownLayoutTypesTo,
    optionsDrillDownLayoutTypesTo } = useSelectors();
  const noResults = !!riskReportResults && (riskReportResults.matrixDefs.length === 0 ||
    riskReportResults.results[0]?.riskMatrices.length === 0);
  // Check if report result contains multiple risk matrix to adjust styling
  const resultHasManyRiskMatrices = riskReportResults?.results.length > 1 || riskReportResults?.results[0]?.riskMatrices?.length > 1;

  const riskMatrices = fetching ? <ElementLoader /> :
    <RiskMatrices
      translation={translation}
      riskReportResults={riskReportResults}
      layoutAttributes={layoutAttributes}
      onDrilldown={onDrilldown}
      workingDefinition={workingDefinition}
      resultHasManyRiskMatrices={resultHasManyRiskMatrices}
      height={height}
    />;

  if (executeFailed) {
    return executeFailed;
  }

  const errorMessagesFromResults = autoExecute && !!riskReportResults?.errorMessages?.length && <div className='alert alert-warning'>
    {riskReportResults?.errorMessages?.map((message, i) => <p key={i}>{message}</p>)}
  </div>;

  const resultOrInfo = autoExecute ? !noResults && riskMatrices : <div className={`${styles.riskMatrixReportCell}`}>
    <CaseReportListingInformation translation={translation} />
  </div>;

  const isPrinting = location?.search.includes('print=true') ? 'printing' : '';

  return <div className={`${styles.riskMatrixReportRow} ${resultHasManyRiskMatrices ? 'many-risk-matrix-results' : ''} ${isPrinting}`}>
    {!hideVisualizationOptions && <div className={`${styles.fullwidthContainer} ${styles.optionsContainer}`}>
      <div className={styles.drilldownToWrapper}>
        <span className={styles.outerLabel}>{translation?.drill_down_to}
          <InfoText className={styles.infoTextWrapper} visible={true} text={translation.drill_down_to_info} />
        </span>
        <div className={styles.drilldownLayoutToWrapper}>
          <SelectSingle
            componentId={'drill-down-to'}
            className={styles.drilldownTo}
            valueItem={selectedDrillDownReport}
            options={optionsDrillDownTo}
            onLoadOptions={onLoadOptionsDrillDown}
            onChange={onSetDrillDownReport}
            textOptionsDone={translation?.drill_down_to_done}
            fetching={fetchingOptionsDrillDownTo}
          />
          {optionsDrillDownLayoutTypesTo?.length > 1 ?
            <SelectSingle
              componentId={'drill-down-layout-to'}
              className={styles.drilldownTo}
              valueItem={selectedDrillDownLayoutType}
              options={transformLayoutOptionsForSelect(optionsDrillDownLayoutTypesTo)}
              onChange={onSetDrillDownLayoutType}
              textOptionsDone={translation?.drill_down_to_done}
              fetching={fetchingOptionsDrillDownLayoutTypesTo}
            />
            : null} 
        </div>
      </div>
      <AttributesPicker
        presentationType={presentationType.riskMatrixListing}
        onAttributeChange={onUpdateLayoutAttributes}
        translations={translation}
        layoutAttributes={layoutAttributes}
      />
    </div>}
    {errorMessagesFromResults}
    {noResults ? <div className={`${caseListingstyles.uiSearchLoader} ${styles.fullwidthContainer}`}>
      {translation.search_no_hits}
    </div>
      :
      resultOrInfo}
  </div>;
};
